//trays-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import { IconButton, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../components/confirm-dialog";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext, ErrorContext } from "../components/app-routes";
import Page from "../components/page";
import { Tray } from "../models/tray";
import { traysApi } from "../services/api/trays";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { Link, useHistory } from "react-router-dom";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

const TraysDataGrid = React.memo(
  (props: {
    trays: Tray[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: any) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const context = useContext(UserContext);
    const grower = context.grower;
    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Trays List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
      },
      {
        field: "soilPerTray",
        headerName: "Soil Per Tray",
        width: 150,
      },
      {
        field: "unit",
        headerName: "Unit",
        width: 120,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 200,
      },
    ];

    if (!props.trays) return null;

    return (
      <StripedDataGrid
        storageKey="trays-grid"
        columns={columns}
        rows={props.trays.map((c) => ({
          id: c.id,
          name: c.name,
          soilPerTray: c.soilPerTray,
          unit: c.unit,
          notes: c.notes,
          archivedAt: c.archivedAt,
        }))}
        initialState={{
          columns: {
            columnVisibilityModel: {
              notes: false,
            },
          },
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              page: props.initialPage ?? 0,
              pageSize: 15,
            },
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

export default function TraysPage() {
  const [trays, setTrays] = useState<Tray[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trayChangedCounter, setTrayChangedCounter] = useState(0);
  const { handleError } = useContext(ErrorContext);
  const showArchivedCheckedStorageKey = "traysShowArchivedChecked";
  const showActiveCheckedStorageKey = "traysShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );
  const history = useHistory();

  useEffect(() => {
    async function getTrays() {
      try {
        const trays = await traysApi.getAll();
        setTrays(trays);
      } catch (error) {
        handleError(error);
      }
    }
    getTrays();
  }, [trayChangedCounter]);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleClickTrash = () => {
    setDialogOpen(true);
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setDialogOpen(false);

    if (!okTrash) {
      return;
    }

    setBusy(true);

    try {
      for (let i = 0; i < selected.length; i++) {
        const tray = getTrayFromId(selected[i]);

        await traysApi.update({
          ...tray,
          archivedAt: DateTime.local().toISO(),
        });

        mixpanel.track("Delete Tray");
      }

      setTrayChangedCounter(trayChangedCounter + selected.length);
      setSelected([]);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const getTrayFromId = (id: number): Tray => {
    const tray = trays?.find((v) => v.id === id);

    if (!tray) {
      throw new Error("Invalid state");
    }

    return tray;
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    const tray = getTrayFromId(id as number);
    history.push(`/home/trays/${tray.id}`);
  };

  let initialPage = getSearchParamNumber(history, "page", 0);

  return (
    <Page title="Trays">
      {trays && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/home/trays/add"
              disabled={busy}
            >
              Add
            </Button>
            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={handleClickTrash}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <TraysDataGrid
              trays={trays.filter(
                (tray) =>
                  (showActiveChecked && tray.archivedAt === null) ||
                  (showArchivedChecked && tray.archivedAt !== null),
              )}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
            />
          </Box>
          {selected && (
            <ConfirmDialog
              title="Trash Trays"
              description={`Are you sure you want to trash ${
                selected.length
              } tray${selected.length > 1 ? "s" : ""}?`}
              open={dialogOpen}
              onClose={handleTrashDialogClose}
            ></ConfirmDialog>
          )}
        </>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
